// Angular
import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// RxJS
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

// Component Library
import { FsxOption, FsxSelectComponent } from 'fsx-component-library';

// Data Contracts
import { ClerkReviewJudge } from '@data-contracts';

// Local
import { PersonNamePipe } from '@app/pipes/person-name.pipe';
import { ClerkReviewFilterFormService } from '@feature/crl/clerk-review-filter-form.service';
import { ClerkReviewFilterPayloadService } from '@feature/crl/clerk-review-filter-payload.service';
import { ClerkReviewFilterStoreValuesService } from '@feature/crl/clerk-review-filter-store-values.service';

/**
 * A constant to use for the default "All Judges" option..
 */
export const ALL_JUDGES_VALUE = 0;

@Component({
  selector: 'app-judge-filter-control',
  standalone: true,
  imports: [ReactiveFormsModule, FsxSelectComponent, FormsModule],
  templateUrl: './judge-filter-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./judge-filter-control.component.scss'],
})
export class JudgeFilterControlComponent implements OnInit, OnDestroy {
  /*********************************************************************************************************************
   * Service Injections
   ********************************************************************************************************************/
  private readonly formService = inject(ClerkReviewFilterFormService);
  private readonly payloadService = inject(ClerkReviewFilterPayloadService);
  private readonly storeValuesService = inject(
    ClerkReviewFilterStoreValuesService,
  );

  /**
   * The collection of objects from which we can derive a collection
   * of dropdown Option objects.
   */
  @Input() judges: ClerkReviewJudge[] = [];

  /**
   * The value of the Option that should be selected by default.
   * - Set to the "All Judges" option value when multiple judge options are available.
   * - Set to single option value when only one judge option is available.
   */
  defaultValue = ALL_JUDGES_VALUE;

  /**
   * The FormControl for the "Judge" dropdown.
   */
  formControl = this.formService.judgeFormControl;

  /**
   * The collection of dropdown Option objects to pass to the FsxSelectComponent.
   */
  judgeOptions: FsxOption<number>[] = [];

  /**
   * The Subject used to teardown the filter control listener subscriptions.
   */
  private destroy$$ = new Subject<void>();

  /**
   * The Angular lifecycle hook where we can safely run any code dependent on @Input() bindings.
   */
  ngOnInit(): void {
    const storedValue = this.storeValuesService.getStoredJudgeValue();
    this.deriveDropdownOptions();
    this.setDefaultValue();
    this.setValue(storedValue);
    this.startListeningForChanges();
  }

  /**
   * The Angular lifecycle hook for tearing down the subscriptions with the component.
   */
  ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  /**
   * A private method to set up the FormControl valueChanges() listener.
   */
  private startListeningForChanges(): void {
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$$), distinctUntilChanged())
      .subscribe((value: number) => {
        // Use the emitted value to set the Judge filter payload.
        this.payloadService.setJudgeFilter(value);
      });
  }

  /**
   * A private method to derive the dropdown Options from the passed in judges collection.
   */
  private deriveDropdownOptions(): void {
    // Derive the Judge Options from the clerkReviewFilterSettings.judges collection.
    this.judgeOptions = this.judges
      .map((judge: ClerkReviewJudge) => {
        const judgeName = new PersonNamePipe().transform(judge.judgeName);
        const option: FsxOption<number> = {
          value: judge.judgeId,
          label: judgeName,
        };
        return option;
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    // Conditionally Prepend the "All Judges" option.
    if (this.judgeOptions.length > 1) {
      // Initialize the "All Judges" Option.
      const allJudgesOption: FsxOption<number> = {
        value: ALL_JUDGES_VALUE,
        label: 'All Judges',
      };

      // Add the "All Judges" Option to the beginning of the judgeOptions collection.
      this.judgeOptions.unshift(allJudgesOption);
    }
  }

  /**
   * A private method to determine which option value should be used as the default value.
   */
  private setDefaultValue(): void {
    // Guard clause to prevent trying to set a value when no dropdown options were derived.
    if (this.judgeOptions.length === 0) {
      console.warn('There must be at least 1 judge option to set a default.');
      return;
    }

    // Set the default value here. Always the first item in the list.
    this.defaultValue = this.judgeOptions[0].value as number;
  }

  /**
   * A public method to allow a new FormControl value to be set.
   * - If no value is provided or if the value is falsy then it falls back to the default value.
   *
   * @param {number | null} newValue - The new value to set.
   */
  setValue(newValue: number | null = null): void {
    const selectedValue = newValue || this.defaultValue; // Use provided value or fallback to default
    this.formControl.setValue(selectedValue); // Update FormControl with the selected value
  }
}
